import React from 'react';
import { Menu, MenuItem, Divider, Typography } from '@mui/material';

type ProfileMenuItemVariant = 'default' | 'primary' | 'muted';
type ProfileMenuActiveItem = 'name-card' | 'setting' | null;

interface ProfileDropdownMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /** Toggles the signed-in (Setting / Sign out) vs signed-out (Sign in) layout. */
  signedIn: boolean;
  onNameCard?: () => void;
  onSetting?: () => void;
  onSignOut?: () => void;
  onSignIn?: () => void;
  onSignInAsAgent?: () => void;
  onSignedInSwitch?: () => void;
  showNameCard?: boolean;
  showSignedInSwitchAccount?: boolean;
  signedInSwitchLabel?: string;
  activeItem?: ProfileMenuActiveItem;
  /** Route-aware label for the sign-in item (e.g. "Sign in as tenant" on B-side). */
  signInLabel?: string;
  /** Hidden for B-side users, who are already agents. */
  showSignInAsAgent?: boolean;
}

// Matches the navbar hover dropdowns (Compare / My Journey / language) so the
// profile menu reads as part of the same family.
const DROPDOWN_WIDTH_PX = 290;
const DROPDOWN_PADDING_X_PX = 10;
const DROPDOWN_PADDING_Y_PX = 20;
const DROPDOWN_ITEM_GAP_PX = 20;
const DROPDOWN_GAP_BELOW_NAV_PX = 16;

const ITEM_VARIANT_SX: Record<ProfileMenuItemVariant, { fontWeight: number; color: string }> = {
  default: { fontWeight: 400, color: '#363434' },
  primary: { fontWeight: 400, color: '#363434' },
  muted: { fontWeight: 400, color: '#7B6F6E' },
};

const ProfileDropdownMenu: React.FC<ProfileDropdownMenuProps> = ({
  anchorEl,
  open,
  onClose,
  signedIn,
  onNameCard,
  onSetting,
  onSignOut,
  onSignIn,
  onSignInAsAgent,
  onSignedInSwitch,
  showNameCard = false,
  showSignedInSwitchAccount,
  signedInSwitchLabel = 'Sign in as agent',
  activeItem = null,
  signInLabel = 'Sign in',
  showSignInAsAgent = true,
}) => {
  const renderItem = (
    key: string,
    label: string,
    onClick: (() => void) | undefined,
    variant: ProfileMenuItemVariant,
    isActive = false
  ) => (
    <MenuItem
      key={key}
      onClick={onClick}
      disableRipple
      sx={{
        p: 0,
        minHeight: 'auto',
        width: '100%',
        justifyContent: 'center',
        '&:hover, &:focus-visible': {
          backgroundColor: 'transparent',
        },
        '&:hover .profile-menu-item-label, &:focus-visible .profile-menu-item-label': {
          color: '#e15600',
        },
      }}
    >
      <Typography
        className="profile-menu-item-label"
        sx={{
          width: '100%',
          fontFamily: 'Montserrat, sans-serif',
          fontSize: '14px',
          lineHeight: 'normal',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          ...ITEM_VARIANT_SX[variant],
          ...(isActive ? { fontWeight: 700, color: '#e15600' } : {}),
        }}
      >
        {label}
      </Typography>
    </MenuItem>
  );

  const divider = (
    <Divider key="agent-divider" sx={{ borderColor: 'rgba(54, 52, 52, 0.15)', m: 0 }} />
  );

  const items: React.ReactNode[] = signedIn
    ? [
        ...(showNameCard ? [renderItem('name-card', 'Name Card', onNameCard, 'primary', activeItem === 'name-card')] : []),
        renderItem('setting', 'Setting', onSetting, 'default', activeItem === 'setting'),
        renderItem('sign-out', 'Sign out', onSignOut, 'primary'),
        ...(showSignedInSwitchAccount ?? showSignInAsAgent
          ? [divider, renderItem('agent', signedInSwitchLabel, onSignedInSwitch || onSignInAsAgent, 'muted')]
          : []),
      ]
    : [
        renderItem('sign-in', signInLabel, onSignIn, 'primary'),
        divider,
        renderItem('agent', 'Sign in as agent', onSignInAsAgent, 'muted'),
      ];

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            width: DROPDOWN_WIDTH_PX,
            minWidth: DROPDOWN_WIDTH_PX,
            maxWidth: DROPDOWN_WIDTH_PX,
            boxSizing: 'border-box',
            marginTop: `${DROPDOWN_GAP_BELOW_NAV_PX}px`,
            px: `${DROPDOWN_PADDING_X_PX}px`,
            py: `${DROPDOWN_PADDING_Y_PX}px`,
            borderRadius: '20px',
            border: '0.5px solid #D3CCC9',
            backgroundColor: 'rgba(211, 204, 201, 0.2)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            boxShadow: '0px 4px 6px 0px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
          },
        },
      }}
      MenuListProps={{
        disablePadding: true,
        sx: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: `${DROPDOWN_ITEM_GAP_PX}px`,
        },
      }}
    >
      {items}
    </Menu>
  );
};

export default ProfileDropdownMenu;
